import { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { LogOut, Menu, User as UserIcon, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/theme-toggle";
import { LanguageToggle } from "@/components/language-toggle";
import { Logo } from "@/components/layout/Logo";
import { AppNavigation } from "@/components/layout/AppSidebar";
import { TeamSwitcher } from "@/components/teams/TeamSwitcher";
import { CompanySwitcher } from "@/components/companies/CompanySwitcher";
import { useAuthUser, useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/cn";

function initialsOf(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Top app bar — logo on the left, the live team switcher in the middle,
 * language + theme toggles + user menu on the right.
 */
export function AppHeader() {
  const user = useAuthUser();
  const { signOut } = useAuth();
  const { t } = useTranslation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const display = user.phone;
  const initials = initialsOf(display);

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 lg:px-6">
      <Dialog open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 lg:hidden"
            aria-label={t("nav.open_menu")}
          >
            <Menu className="h-5 w-5" />
          </Button>
        </DialogTrigger>
        <DialogPortal>
          <DialogOverlay className="lg:hidden" />
          <DialogPrimitive.Content
            className={cn(
              "fixed inset-y-0 left-0 z-50 grid w-80 max-w-[calc(100vw-2rem)] grid-rows-[auto_1fr] overflow-hidden border-r bg-background shadow-lg duration-200",
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left lg:hidden",
            )}
          >
            <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
              <Logo />
              <DialogTitle className="sr-only">
                {t("nav.mobile_title")}
              </DialogTitle>
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  aria-label={t("nav.close_menu")}
                >
                  <X className="h-4 w-4" />
                </Button>
              </DialogClose>
            </div>
            <AppNavigation
              className="min-h-0 overflow-y-auto px-3 py-4"
              onNavigate={() => setMobileNavOpen(false)}
            />
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
      <Logo compact />
      <Separator orientation="vertical" className="hidden h-6 sm:block" />
      <TeamSwitcher />
      <Separator orientation="vertical" className="hidden h-6 sm:block" />
      <CompanySwitcher />
      <div className="ml-auto flex items-center gap-1">
        <LanguageToggle />
        <ThemeToggle />
        <Separator orientation="vertical" className="mx-1 h-6" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              aria-label={t("user.menu")}
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="text-xs font-normal text-muted-foreground">
                {t("home.authenticated_as")}
              </span>
              <span className="font-medium">{display}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile" className="flex items-center gap-2 w-full cursor-pointer">
                <UserIcon className="h-4 w-4" />
                {t("nav.profile")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()}>
              <LogOut className="h-4 w-4" />
              {t("app.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
